import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Get } from "../config/api/get";
import { Post } from "../config/api/post";
import { useSelector } from "react-redux";
import { FEES } from "../config/constants";

const FEES_KEY = ["fees"];

const invalidateFees = (queryClient, id) => {
  queryClient.invalidateQueries({ queryKey: FEES_KEY });
  if (id) queryClient.invalidateQueries({ queryKey: ["fee", id] });
};

export const useGetAllFees = (params = {}) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: [...FEES_KEY, params],
    queryFn: async ({ signal }) => {
      const response = await Get(FEES.getAllVouchers, token, params, { signal });
      return response?.data || { docs: [], totalPages: 1, totalDocs: 0 };
    },
    enabled: !!token,
    placeholderData: keepPreviousData,
  });
};

export const useGetFeeById = (id) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: ["fee", id],
    queryFn: async () => {
      const response = await Get(FEES.getVoucherById + id, token);
      return response?.data?.fee;
    },
    enabled: !!token && !!id,
  });
};

export const useAddFee = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (payload) => {
      const response = await Post(FEES.createVoucher, payload, token);
      if (response?.data?.status) return response.data;
      throw new Error(response?.data?.message || "Failed to create fee voucher");
    },
    onSuccess: () => invalidateFees(queryClient),
  });
};

export const useUpdateFee = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const response = await Post(FEES.updateVoucher + id, payload, token);
      if (response?.data?.status !== false) return response.data;
      throw new Error(response?.data?.message || "Failed to update fee voucher");
    },
    onSuccess: (_data, variables) => invalidateFees(queryClient, variables?.id),
  });
};

export const useToggleFeeStatus = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (id) => {
      const response = await Get(FEES.toggleStatus + id, token);
      if (response?.status !== false) return response;
      throw new Error(response?.message || "Failed to update payment status");
    },
    onSuccess: (_data, id) => invalidateFees(queryClient, id),
  });
};

export const useDeleteFee = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (id) => {
      const response = await Get(FEES.deleteVoucher + id, token);
      if (response?.status !== false) return response;
      throw new Error(response?.message || "Failed to delete fee voucher");
    },
    onSuccess: () => invalidateFees(queryClient),
  });
};

export const usePreviewMonth = () => {
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (payload) => {
      const response = await Post(FEES.previewMonth, payload, token);
      if (response?.data?.status === false) {
        throw new Error(response?.data?.message || "Could not preview vouchers");
      }
      return response?.data?.data || { included: [], skipped: [] };
    },
  });
};

export const useSendMonth = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (payload) => {
      const response = await Post(FEES.sendMonth, payload, token);
      if (response?.data?.status === false) {
        throw new Error(response?.data?.message || "No vouchers to send");
      }
      return response?.data?.data || response?.data;
    },
    onSuccess: () => invalidateFees(queryClient),
  });
};

export const useReviewFee = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async ({ id, action, rejectReason }) => {
      const response = await Post(FEES.review + id, { action, rejectReason }, token);
      if (response?.data?.status === false) {
        throw new Error(response?.data?.message || "Could not update voucher");
      }
      return response.data;
    },
    onSuccess: (_data, variables) => invalidateFees(queryClient, variables?.id),
  });
};
